import { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import api from "../../lib/api";
import { toast } from "sonner";

const MIN_COMPS = 3;
const MAX_COMPS = 8;

/**
 * Admin-only scorecard template editor (Phase 4a).
 * Renders inside Settings.jsx as a section. Left column lists templates
 * (default first, then per-role); right column edits the selected template.
 *
 * Behaviors:
 *  - Workspace default template name is editable but cannot be deleted
 *  - Per-role templates can be created (one per role), edited, archived
 *  - Add/remove competencies enforces 3..8 server-side; UI disables buttons at boundaries
 *  - Save = PUT; new = POST; delete = soft-delete via DELETE
 *  - Concurrent-edit protection: PATCHes whole competencies array, server
 *    increments template_version
 */
export default function ScorecardTemplatesPanel({ readonly }) {
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);  // editable copy of selected template
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [tplResp, rolesResp] = await Promise.all([
        api.get("/scorecard-templates"),
        api.get("/roles").catch(() => ({ data: [] })),
      ]);
      setTemplates(tplResp.data);
      setRoles(rolesResp.data || []);
      if (!selectedId && tplResp.data.length > 0) {
        setSelectedId(tplResp.data[0].scorecard_template_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Rehydrate draft when the user picks a different template
  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    const t = templates.find(x => x.scorecard_template_id === selectedId);
    if (t) {
      setDraft({
        scorecard_template_id: t.scorecard_template_id,
        name: t.name,
        is_default: t.is_default,
        role_id: t.role_id,
        template_version: t.template_version,
        competencies: t.competencies.map(c => ({ ...c })),  // clone
      });
    }
  }, [selectedId, templates]);

  const updateCompetency = (idx, partial) => {
    setDraft(d => d && {
      ...d,
      competencies: d.competencies.map((c, i) => i === idx ? { ...c, ...partial } : c),
    });
  };

  const addCompetency = () => {
    setDraft(d => d && d.competencies.length < MAX_COMPS ? {
      ...d,
      competencies: [...d.competencies, { name: "", description: "", order: d.competencies.length + 1 }],
    } : d);
  };

  const removeCompetency = (idx) => {
    setDraft(d => d && d.competencies.length > MIN_COMPS ? {
      ...d,
      competencies: d.competencies.filter((_, i) => i !== idx).map((c, i) => ({ ...c, order: i + 1 })),
    } : d);
  };

  const moveCompetency = (idx, delta) => {
    setDraft(d => {
      if (!d) return d;
      const target = idx + delta;
      if (target < 0 || target >= d.competencies.length) return d;
      const next = [...d.competencies];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, competencies: next.map((c, i) => ({ ...c, order: i + 1 })) };
    });
  };

  const save = async () => {
    if (!draft) return;
    if (draft.competencies.some(c => !c.name.trim())) {
      toast.error("Every competency needs a name");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        competencies: draft.competencies.map(c => ({
          name: c.name.trim(),
          description: (c.description || "").trim(),
        })),
      };
      const { data } = await api.put(`/scorecard-templates/${draft.scorecard_template_id}`, body);
      setTemplates(ts => ts.map(t => t.scorecard_template_id === data.scorecard_template_id ? data : t));
      toast.success(`Template saved (v${data.template_version})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const createNew = async () => {
    const roleId = prompt(
      "Role (id) to scope this template to (leave blank for unscoped):\n\n" +
      roles.map(r => `${r.role_id} — ${r.name}`).join("\n")
    );
    const name = prompt("Template name (max 120 chars):", "New scorecard template");
    if (!name) return;
    try {
      const { data } = await api.post("/scorecard-templates", {
        name,
        role_id: roleId || null,
        competencies: [
          { name: "Technical Skills" },
          { name: "Communication" },
          { name: "Role Alignment" },
        ],
      });
      setTemplates(ts => [...ts, data]);
      setSelectedId(data.scorecard_template_id);
      toast.success("Template created");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail
        : detail?.error === "template_for_role_already_exists" ? "A template for that role already exists"
        : "Couldn't create template"
      );
    }
  };

  const archive = async () => {
    if (!draft) return;
    if (draft.is_default) { toast.error("Default template cannot be deleted"); return; }
    if (!confirm("Archive this template? Existing scorecards keep their snapshot.")) return;
    try {
      await api.delete(`/scorecard-templates/${draft.scorecard_template_id}`);
      setTemplates(ts => ts.filter(t => t.scorecard_template_id !== draft.scorecard_template_id));
      setSelectedId(templates.find(t => t.is_default)?.scorecard_template_id || null);
      toast.success("Template archived");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  if (loading) return <div className="p-6 text-[13px] text-ink-500">Loading scorecard templates…</div>;

  return (
    <div className="grid grid-cols-12 gap-6 p-6" data-testid="scorecard-templates-panel">
      <div className="col-span-4 space-y-1">
        <div className="text-[11px] font-medium uppercase tracking-wider text-ink-500 mb-2">Templates</div>
        {templates.map(t => (
          <button
            key={t.scorecard_template_id}
            type="button"
            onClick={() => setSelectedId(t.scorecard_template_id)}
            className={`w-full text-left rounded-md border px-3 py-2 transition ${
              selectedId === t.scorecard_template_id
                ? "border-emerald-700 bg-emerald-50/40"
                : "border-ink-200 bg-white hover:border-ink-300"
            }`}
            data-testid={`template-list-item-${t.scorecard_template_id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-medium text-ink-900 truncate">{t.name}</span>
              {t.is_default && (
                <span className="text-[10px] uppercase tracking-wide text-emerald-700">default</span>
              )}
            </div>
            <div className="text-[11px] text-ink-500 mt-0.5">
              {t.role_id
                ? (roles.find(r => r.role_id === t.role_id)?.name || "Role")
                : "Workspace-wide"} · {t.competencies.length} competencies
            </div>
          </button>
        ))}
        {!readonly && (
          <Button
            variant="outline" onClick={createNew} size="sm"
            className="mt-3 w-full border-dashed text-[13px]"
            data-testid="template-new-button"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New template
          </Button>
        )}
      </div>

      <div className="col-span-8">
        {!draft ? (
          <div className="text-[13px] text-ink-500">Select a template to edit.</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-ink-500">Template name</label>
              <Input
                value={draft.name}
                disabled={readonly}
                maxLength={120}
                onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                className="mt-1 hp-input"
                data-testid="template-name-input"
              />
              <div className="mt-1 text-[11px] text-ink-400">
                v{draft.template_version} · {draft.is_default ? "Workspace default (cannot be deleted)" : draft.role_id ? `Scoped to role` : "Unscoped"}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-medium uppercase tracking-wider text-ink-500">
                  Competencies · {draft.competencies.length}/{MAX_COMPS}
                </label>
                {!readonly && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={addCompetency}
                    disabled={draft.competencies.length >= MAX_COMPS}
                    className="text-[12px] text-emerald-800"
                    data-testid="template-add-competency"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {draft.competencies.map((c, idx) => (
                  <div key={idx} className="flex items-start gap-2 rounded-md border border-ink-200 bg-white p-3" data-testid={`template-competency-${idx}`}>
                    <div className="flex flex-col gap-0.5 pt-1 text-ink-400">
                      <button type="button" onClick={() => moveCompetency(idx, -1)} disabled={readonly || idx === 0} className="hover:text-ink-700 disabled:opacity-30" data-testid={`template-competency-up-${idx}`}>
                        <GripVertical className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Competency name"
                        value={c.name}
                        disabled={readonly}
                        maxLength={100}
                        onChange={(e) => updateCompetency(idx, { name: e.target.value })}
                        className="hp-input text-[14px]"
                        data-testid={`template-competency-name-${idx}`}
                      />
                      <Textarea
                        placeholder="Optional description"
                        value={c.description || ""}
                        disabled={readonly}
                        maxLength={500}
                        rows={2}
                        onChange={(e) => updateCompetency(idx, { description: e.target.value })}
                        className="text-[12px] resize-y"
                        data-testid={`template-competency-desc-${idx}`}
                      />
                    </div>
                    {!readonly && (
                      <button
                        type="button"
                        onClick={() => removeCompetency(idx)}
                        disabled={draft.competencies.length <= MIN_COMPS}
                        className="mt-1 text-ink-400 hover:text-red-600 disabled:opacity-30"
                        title={draft.competencies.length <= MIN_COMPS ? `Min ${MIN_COMPS} competencies` : "Remove"}
                        data-testid={`template-competency-remove-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {!readonly && (
              <div className="flex items-center justify-between pt-2">
                {draft.is_default ? (
                  <span className="text-[11px] text-ink-400">Default template cannot be deleted</span>
                ) : (
                  <Button variant="ghost" size="sm" onClick={archive} className="text-red-700 hover:text-red-800 text-[12px]" data-testid="template-archive-button">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Archive
                  </Button>
                )}
                <Button
                  onClick={save}
                  disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="template-save-button"
                >
                  {saving ? "Saving…" : "Save template"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
